import { Container } from 'pixi.js';
import { Platform, PlatformSize } from './Platform';
import { ScorePopup } from './ScorePopup';

export class PlatformManager extends Container {
  private platforms: Platform[] = [];
  private scorePopups: ScorePopup[] = [];
  private gameWidth: number;
  private platformY: number;
  private scrollSpeed = 300;
  private platformSpeedMultiplier = 0.65; // Platforms move at 65% of obstacle speed
  private spawnTimer = 0;
  private spawnIntervalMin = 8;
  private spawnIntervalMax = 15;
  private currentSpawnInterval = 0;
  private onTreatCollected?: (points: number) => void;

  constructor(gameWidth: number, groundY: number) {
    super();
    this.gameWidth = gameWidth;
    this.platformY = groundY - 120; // Fixed height slightly below long jump peak
    this.currentSpawnInterval = this.getRandomSpawnInterval();
  }

  private getRandomSpawnInterval(): number {
    return this.spawnIntervalMin + Math.random() * (this.spawnIntervalMax - this.spawnIntervalMin);
  }

  private getRandomPlatformSize(): PlatformSize {
    const sizes: PlatformSize[] = ['short', 'medium', 'long'];
    return sizes[Math.floor(Math.random() * sizes.length)];
  }

  update(deltaTime: number): void {
    this.spawnTimer += deltaTime;

    if (this.spawnTimer >= this.currentSpawnInterval) {
      this.spawnPlatform();
      this.spawnTimer = 0;
      this.currentSpawnInterval = this.getRandomSpawnInterval();
    }

    for (let i = this.platforms.length - 1; i >= 0; i--) {
      const platform = this.platforms[i];
      platform.update(this.scrollSpeed * this.platformSpeedMultiplier, deltaTime);

      if (platform.isOffScreen()) {
        this.removeChild(platform);
        this.platforms.splice(i, 1);
      }
    }

    // Update score popups
    for (let i = this.scorePopups.length - 1; i >= 0; i--) {
      const popup = this.scorePopups[i];
      popup.update(deltaTime);

      if (popup.isComplete()) {
        this.removeChild(popup);
        this.scorePopups.splice(i, 1);
      }
    }
  }

  private spawnPlatform(): void {
    const size = this.getRandomPlatformSize();
    // Create platform first to get its width
    const platform = new Platform(0, this.platformY, size);

    // Spawn completely off-screen (account for platform width)
    const fullHitbox = platform.getFullHitbox();
    const platformWidth = fullHitbox.width;
    platform.x = this.gameWidth + platformWidth / 2 + 50;

    this.platforms.push(platform);
    this.addChild(platform);
  }

  checkPlatformCollisions(
    playerHitbox: { x: number; y: number; width: number; height: number },
    velocityY: number
  ): { type: 'none' | 'land' | 'bonk'; platform?: Platform } {
    for (const platform of this.platforms) {
      // Check landing (falling down, hitting top of platform)
      if (velocityY > 0) {
        const topHitbox = platform.getTopHitbox();
        if (
          playerHitbox.x < topHitbox.x + topHitbox.width &&
          playerHitbox.x + playerHitbox.width > topHitbox.x &&
          playerHitbox.y < topHitbox.y + topHitbox.height &&
          playerHitbox.y + playerHitbox.height > topHitbox.y
        ) {
          return { type: 'land', platform };
        }
      }

      // Check bonking (jumping up, hitting bottom of platform)
      if (velocityY < 0) {
        const bottomHitbox = platform.getBottomHitbox();
        if (
          playerHitbox.x < bottomHitbox.x + bottomHitbox.width &&
          playerHitbox.x + playerHitbox.width > bottomHitbox.x &&
          playerHitbox.y < bottomHitbox.y + bottomHitbox.height &&
          playerHitbox.y + playerHitbox.height > bottomHitbox.y
        ) {
          return { type: 'bonk', platform };
        }
      }
    }

    return { type: 'none' };
  }

  checkTreatCollisions(
    playerX: number,
    playerY: number,
    playerHitbox: { x: number; y: number; width: number; height: number }
  ): void {
    for (const platform of this.platforms) {
      // Calculate player center Y (playerY is at the bottom)
      const playerCenterY = playerY - playerHitbox.height / 2;

      const treat = platform.checkTreatCollision(playerHitbox, playerX, playerCenterY);
      if (treat && this.onTreatCollected) {
        // Get treat world position for popup
        const treatWorldX = platform.x + treat.x;
        const treatWorldY = platform.y + treat.y;

        // Create score popup at treat location
        const popup = new ScorePopup(treatWorldX, treatWorldY, 20);
        this.scorePopups.push(popup);
        this.addChild(popup);

        // Award points
        this.onTreatCollected(20);
      }
    }
  }

  setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
  }

  updateDimensions(gameWidth: number, groundY: number): void {
    this.gameWidth = gameWidth;
    this.platformY = groundY - 120;
  }

  reset(): void {
    for (const platform of this.platforms) {
      this.removeChild(platform);
    }
    this.platforms = [];

    for (const popup of this.scorePopups) {
      this.removeChild(popup);
    }
    this.scorePopups = [];

    this.spawnTimer = 0;
    this.currentSpawnInterval = this.getRandomSpawnInterval();
  }

  onTreatCollectedCallback(callback: (points: number) => void): void {
    this.onTreatCollected = callback;
  }

  getPlatforms(): Platform[] {
    return this.platforms;
  }
}
